/**
 * Controles de pausa automática (circuit) — só tenant AWS própria (BYO).
 * GET/PATCH /api/v1/me/byo-circuit-settings — interceptor já unwrap envelope.
 * Base URL: config.api.API_URL (não url_helper — paths só; API_URL vive em config).
 */
import axios from "axios";
import config from "../config";
import type { ApiSuccess } from "./api_helper";

const apiBase = config.api.API_URL;

export type ByoCircuitWarning = {
  level: "info" | "warning" | "danger" | string;
  code: string;
  message: string;
};

export type ByoCircuitBounds = {
  bounce: { min: number; recommended: number; max: number };
  complaint: { min: number; recommended: number; max: number };
  min_sent: { min: number; recommended: number; max: number };
};

export type ByoCircuitPresetKey = "conservative" | "recommended" | "aggressive";

export type ByoCircuitPresetValues = {
  bounce_pct: number;
  complaint_pct: number;
  min_sent: number;
  auto_resume_enabled: boolean;
};

export type ByoCircuitHistoryItem = {
  action: string;
  at: string | null;
  actor: string | null;
  summary?: string | null;
  old?: Record<string, unknown> | null;
  new?: Record<string, unknown> | null;
};

export type ByoCircuitSettings = {
  available: boolean;
  sending_account_mode: "platform" | "customer_aws" | string;
  bounds: ByoCircuitBounds;
  presets?: Record<ByoCircuitPresetKey, ByoCircuitPresetValues>;
  recommended: ByoCircuitPresetValues;
  platform_defaults: ByoCircuitPresetValues;
  values: {
    bounce_pct: number | null;
    complaint_pct: number | null;
    min_sent: number | null;
    auto_resume_enabled: boolean | null;
    using_platform_defaults: boolean;
  };
  effective: {
    bounce_pct: number;
    complaint_pct: number;
    min_sent: number;
    auto_resume_enabled: boolean;
    source: "platform" | "byo_override" | string;
  };
  near_aws_ceiling?: boolean;
  aggressive_blocked?: boolean;
  recent_24h?: {
    sent: number;
    bounce_pct: number;
    complaint_pct: number;
  };
  recent_7d?: {
    sent: number;
    bounce_pct: number;
    complaint_pct: number;
  };
  preview?: {
    would_pause: boolean;
    enough_volume: boolean;
    reasons: string[];
  } | null;
  history?: ByoCircuitHistoryItem[];
  warnings: ByoCircuitWarning[];
};

export type UpdateByoCircuitPayload = {
  bounce_pct?: number | null;
  complaint_pct?: number | null;
  min_sent?: number | null;
  auto_resume_enabled?: boolean | null;
  reset_to_defaults?: boolean;
  preset?: ByoCircuitPresetKey;
};

export const getByoCircuitSettings = (): Promise<
  ApiSuccess<ByoCircuitSettings>
> => axios.get(apiBase + "/me/byo-circuit-settings");

export const updateByoCircuitSettings = (
  payload: UpdateByoCircuitPayload
): Promise<ApiSuccess<ByoCircuitSettings>> =>
  axios.patch(apiBase + "/me/byo-circuit-settings", payload);

/** Prévia local enquanto o formulário muda (mesma regra do backend). */
export const previewWouldPause = (
  recent: { sent: number; bounce_pct: number; complaint_pct: number },
  bouncePct: number,
  complaintPct: number,
  minSent: number
): { would_pause: boolean; enough_volume: boolean; reasons: string[] } => {
  const enough = recent.sent >= minSent;
  const reasons: string[] = [];
  if (enough && recent.bounce_pct >= bouncePct) reasons.push("bounce");
  if (enough && recent.complaint_pct >= complaintPct) reasons.push("complaint");
  return { would_pause: reasons.length > 0, enough_volume: enough, reasons };
};
